import { Fragment } from "react";
import { Helmet } from "react-helmet";
import { Link as RouterLink } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import ContainerPage from "../../components/Containers/ContainerPage";
import {
  CREATE_CAMPAIGN_LIST_PATH,
  CREATE_CAMPAIGN_QUERY_PARAM,
} from "../../components/Modal/ModalCreateAdsCampaign/createAdsCampaignWizardConstants";
import TitlePage from "../../components/Texts/TitlePage";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { getNameBusinessTheme } from "../../actions/getters";
import stepConnectTikTok from "../../assets/guides/tiktok-ad-account/01-connect-tiktok.png";
import stepTikTokAuthorization from "../../assets/guides/tiktok-ad-account/01b-tiktok-authorization.png";
import stepCreateAdAccount from "../../assets/guides/tiktok-ad-account/02-create-ad-account.png";
import stepAdvertiserDetails from "../../assets/guides/tiktok-ad-account/03-advertiser-details.png";
import stepLaunchCampaign from "../../assets/guides/tiktok-ad-account/05-launch-campaign.png";
import {
  BUTTON_CREATE_CAMPAIGN2,
  GUIDE_TIKTOK_AD_ACCOUNT_BACK,
  GUIDE_TIKTOK_AD_ACCOUNT_CHECKLIST_BILLING,
  GUIDE_TIKTOK_AD_ACCOUNT_CHECKLIST_PAYMENT,
  GUIDE_TIKTOK_AD_ACCOUNT_CHECKLIST_TITLE,
  GUIDE_TIKTOK_AD_ACCOUNT_CHECKLIST_WEBSITE,
  GUIDE_TIKTOK_AD_ACCOUNT_EYEBROW,
  GUIDE_TIKTOK_AD_ACCOUNT_INTRO,
  GUIDE_TIKTOK_AD_ACCOUNT_PATH_AD_ACCOUNTS,
  GUIDE_TIKTOK_AD_ACCOUNT_PATH_ADS_MANAGER,
  GUIDE_TIKTOK_AD_ACCOUNT_PATH_CONNECT,
  GUIDE_TIKTOK_AD_ACCOUNT_PATH_CREATE,
  GUIDE_TIKTOK_AD_ACCOUNT_PATH_SETTINGS,
  GUIDE_TIKTOK_AD_ACCOUNT_STEP,
  GUIDE_TIKTOK_AD_ACCOUNT_STEP_1_ALT,
  GUIDE_TIKTOK_AD_ACCOUNT_STEP_1_AUTH_ALT,
  GUIDE_TIKTOK_AD_ACCOUNT_STEP_1_BODY,
  GUIDE_TIKTOK_AD_ACCOUNT_STEP_1_TITLE,
  GUIDE_TIKTOK_AD_ACCOUNT_STEP_2_ALT,
  GUIDE_TIKTOK_AD_ACCOUNT_STEP_2_BODY,
  GUIDE_TIKTOK_AD_ACCOUNT_STEP_2_TITLE,
  GUIDE_TIKTOK_AD_ACCOUNT_STEP_3_ALT,
  GUIDE_TIKTOK_AD_ACCOUNT_STEP_3_BODY,
  GUIDE_TIKTOK_AD_ACCOUNT_STEP_3_TITLE,
  GUIDE_TIKTOK_AD_ACCOUNT_STEP_4_BODY,
  GUIDE_TIKTOK_AD_ACCOUNT_STEP_4_TITLE,
  GUIDE_TIKTOK_AD_ACCOUNT_STEP_5_ALT,
  GUIDE_TIKTOK_AD_ACCOUNT_STEP_5_BODY,
  GUIDE_TIKTOK_AD_ACCOUNT_STEP_5_TITLE,
  GUIDE_TIKTOK_AD_ACCOUNT_TITLE,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_BUSINESS_CENTER,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_COMPANY,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_COUNTRY,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_CURRENCY,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_INDUSTRY,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_NAME,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_TIMEZONE,
} from "../../i18n/keysTranslations";

const TIKTOK_BUSINESS_ADVERTISER_ACCOUNTS_URL =
  "https://business.tiktok.com/manage/accounts/adv";
const TIKTOK_BUSINESS_LINK_TOKEN = "___TIKTOK_BUSINESS___";
const TIKTOK_BUSINESS_LINK_LABEL = "TikTok Business";

const PATH_KEYS = [
  GUIDE_TIKTOK_AD_ACCOUNT_PATH_SETTINGS,
  GUIDE_TIKTOK_AD_ACCOUNT_PATH_AD_ACCOUNTS,
  GUIDE_TIKTOK_AD_ACCOUNT_PATH_CONNECT,
  GUIDE_TIKTOK_AD_ACCOUNT_PATH_CREATE,
  GUIDE_TIKTOK_AD_ACCOUNT_PATH_ADS_MANAGER,
];

const ADVERTISER_FIELD_KEYS = [
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_BUSINESS_CENTER,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_NAME,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_COMPANY,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_INDUSTRY,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_COUNTRY,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_CURRENCY,
  MODAL_CREATE_AD_ACCOUNT_TIKTOK_TIMEZONE,
];

const CHECKLIST_KEYS = [
  GUIDE_TIKTOK_AD_ACCOUNT_CHECKLIST_WEBSITE,
  GUIDE_TIKTOK_AD_ACCOUNT_CHECKLIST_BILLING,
  GUIDE_TIKTOK_AD_ACCOUNT_CHECKLIST_PAYMENT,
];

const STEPS = [
  {
    titleKey: GUIDE_TIKTOK_AD_ACCOUNT_STEP_1_TITLE,
    bodyKey: GUIDE_TIKTOK_AD_ACCOUNT_STEP_1_BODY,
    altKey: GUIDE_TIKTOK_AD_ACCOUNT_STEP_1_ALT,
    image: stepConnectTikTok,
    imageMaxHeight: 460,
    extraImages: [
      {
        src: stepTikTokAuthorization,
        altKey: GUIDE_TIKTOK_AD_ACCOUNT_STEP_1_AUTH_ALT,
      },
    ],
  },
  {
    titleKey: GUIDE_TIKTOK_AD_ACCOUNT_STEP_2_TITLE,
    bodyKey: GUIDE_TIKTOK_AD_ACCOUNT_STEP_2_BODY,
    altKey: GUIDE_TIKTOK_AD_ACCOUNT_STEP_2_ALT,
    image: stepCreateAdAccount,
  },
  {
    titleKey: GUIDE_TIKTOK_AD_ACCOUNT_STEP_3_TITLE,
    bodyKey: GUIDE_TIKTOK_AD_ACCOUNT_STEP_3_BODY,
    altKey: GUIDE_TIKTOK_AD_ACCOUNT_STEP_3_ALT,
    image: stepAdvertiserDetails,
    fields: true,
  },
  {
    titleKey: GUIDE_TIKTOK_AD_ACCOUNT_STEP_4_TITLE,
    bodyKey: GUIDE_TIKTOK_AD_ACCOUNT_STEP_4_BODY,
    bodyHasLink: true,
    checklist: true,
  },
  {
    titleKey: GUIDE_TIKTOK_AD_ACCOUNT_STEP_5_TITLE,
    bodyKey: GUIDE_TIKTOK_AD_ACCOUNT_STEP_5_BODY,
    altKey: GUIDE_TIKTOK_AD_ACCOUNT_STEP_5_ALT,
    image: stepLaunchCampaign,
    bodyHasLink: true,
    createCampaign: true,
  },
];

const GuideImage = ({ src, alt, maxHeight }) => (
  <Box
    sx={{
      mt: 2.5,
      borderRadius: "14px",
      overflow: "hidden",
      border: "1px solid #ececec",
      bgcolor: "#f7f7f7",
      lineHeight: 0,
      maxHeight: {
        xs: "none",
        md: maxHeight || "none",
      },
    }}
  >
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        width: "100%",
        height: "auto",
        display: "block",
      }}
    />
  </Box>
);

const cardSx = {
  bgcolor: "#fff",
  border: "1px solid #ececec",
  borderRadius: "20px",
  p: { xs: 2, sm: 3 },
};

const TikTokAdAccountGuide = () => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { theme } = useSelectorApp((state) => state.ui);
  const businessName = theme.nameBusiness || dispatch(getNameBusinessTheme());

  return (
    <>
      <Helmet>
        <title>{`${businessName} - ${t(GUIDE_TIKTOK_AD_ACCOUNT_TITLE)}`}</title>
      </Helmet>
      <ContainerPage hasMarginBottom={false} sx={{ width: "100%" }}>
        <Box
          sx={{
            width: "100%",
            maxWidth: 980,
            mx: "auto",
            mt: { xs: 3, sm: 5 },
            pb: 12,
            px: { xs: 0, sm: 1 },
            pr: { xs: 4, sm: 1 },
          }}
        >
          <Typography
            component={RouterLink}
            to="/settings?tab=accounts"
            variant="body2"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              mb: 2.5,
              color: "text.secondary",
              fontWeight: 600,
              textDecoration: "none",
              "&:hover": { color: "text.primary" },
            }}
          >
            <ChevronLeft size={16} />
            {t(GUIDE_TIKTOK_AD_ACCOUNT_BACK)}
          </Typography>

          <Typography
            sx={{
              display: "block",
              fontSize: "0.8125rem",
              fontWeight: 700,
              color: "text.secondary",
              lineHeight: 1.4,
            }}
          >
            {t(GUIDE_TIKTOK_AD_ACCOUNT_EYEBROW)}
          </Typography>
          <TitlePage sx={{ mt: 0.5, mb: 1.5 }}>
            {t(GUIDE_TIKTOK_AD_ACCOUNT_TITLE)}
          </TitlePage>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 680, lineHeight: 1.65 }}
          >
            {t(GUIDE_TIKTOK_AD_ACCOUNT_INTRO, { NAME_BUSINESS: businessName })}
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 0.75,
              mt: 3,
              mb: 4,
            }}
          >
            {PATH_KEYS.map((key, index) => (
              <Fragment key={key}>
                {index > 0 ? <ChevronRight size={14} color="#9a9a9a" /> : null}
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.6,
                    borderRadius: "999px",
                    border: "1px solid #e6e6e6",
                    bgcolor: "#fff",
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    lineHeight: 1.3,
                  }}
                >
                  {t(key)}
                </Box>
              </Fragment>
            ))}
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {STEPS.map((step, index) => (
              <Box key={step.titleKey} sx={cardSx}>
                <Box
                  sx={{ display: "flex", gap: 1.75, alignItems: "flex-start" }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      bgcolor: "#171717",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        fontWeight: 700,
                        color: "text.secondary",
                        mb: 0.4,
                      }}
                    >
                      {t(GUIDE_TIKTOK_AD_ACCOUNT_STEP, { step: index + 1 })}
                    </Typography>
                    <Typography
                      component="h2"
                      sx={{
                        fontSize: { xs: "1.15rem", sm: "1.35rem" },
                        fontWeight: 600,
                        lineHeight: 1.3,
                        mb: 1,
                      }}
                    >
                      {t(step.titleKey)}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ lineHeight: 1.65, maxWidth: 720 }}
                    >
                      {step.bodyHasLink
                        ? t(step.bodyKey, { NAME_BUSINESS: businessName })
                            .split(TIKTOK_BUSINESS_LINK_TOKEN)
                            .map((part, partIndex, parts) => (
                              <Fragment key={`${step.bodyKey}-${partIndex}`}>
                                {part}
                                {partIndex < parts.length - 1 ? (
                                  <Box
                                    component="a"
                                    href={
                                      TIKTOK_BUSINESS_ADVERTISER_ACCOUNTS_URL
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      color: "inherit",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    {TIKTOK_BUSINESS_LINK_LABEL}
                                  </Box>
                                ) : null}
                              </Fragment>
                            ))
                        : t(step.bodyKey, { NAME_BUSINESS: businessName })}
                    </Typography>
                    {step.createCampaign ? (
                      <Box
                        component={RouterLink}
                        to={`${CREATE_CAMPAIGN_LIST_PATH}?${CREATE_CAMPAIGN_QUERY_PARAM}=1`}
                        sx={{
                          display: "inline-flex",
                          mt: 2,
                          px: 2,
                          py: 1,
                          borderRadius: "999px",
                          bgcolor: "#171717",
                          color: "#fff",
                          fontWeight: 600,
                          fontSize: "0.875rem",
                          textDecoration: "none",
                          "&:hover": { bgcolor: "#000" },
                        }}
                      >
                        {t(BUTTON_CREATE_CAMPAIGN2)}
                      </Box>
                    ) : null}

                    {step.fields ? (
                      <Box sx={{ mt: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          {ADVERTISER_FIELD_KEYS.map((fieldKey) => (
                            <Box
                              key={fieldKey}
                              sx={{
                                px: 1.25,
                                py: 0.7,
                                borderRadius: "10px",
                                bgcolor: "#f6f6f6",
                                fontSize: "0.8125rem",
                                fontWeight: 600,
                              }}
                            >
                              {t(fieldKey)}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ) : null}

                    {step.checklist ? (
                      <Box sx={{ mt: 2.5 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700, mb: 1.25 }}
                        >
                          {t(GUIDE_TIKTOK_AD_ACCOUNT_CHECKLIST_TITLE)}
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "repeat(3, minmax(0, 1fr))",
                            },
                            gap: 1,
                          }}
                        >
                          {CHECKLIST_KEYS.map((itemKey) => (
                            <Box
                              key={itemKey}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1.5,
                                py: 1.25,
                                borderRadius: "12px",
                                bgcolor: "#f6f6f6",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 22,
                                  height: 22,
                                  borderRadius: "50%",
                                  bgcolor: "#171717",
                                  color: "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <Check size={13} strokeWidth={2.5} />
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                              >
                                {t(itemKey)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ) : null}
                  </Box>
                </Box>

                {step.image ? (
                  <GuideImage
                    src={step.image}
                    alt={t(step.altKey, { NAME_BUSINESS: businessName })}
                    maxHeight={step.imageMaxHeight}
                  />
                ) : null}
                {step.extraImages?.map((extraImage) => (
                  <GuideImage
                    key={extraImage.altKey}
                    src={extraImage.src}
                    alt={t(extraImage.altKey)}
                    maxHeight={extraImage.maxHeight}
                  />
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </ContainerPage>
    </>
  );
};

export default TikTokAdAccountGuide;
